import { useState } from "react";
import { database } from "../../firebase";
import { ref, set, push, get, update } from "firebase/database";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import useIsMobile from "../../hooks/useIsMobile";
import { Roles } from "../../constants/roles";
import { Statuses } from "../../constants/statuses";
import parseDate from "../../utils/dateUtils";
import { anatomyConstants } from "../../constants/anatomyConstants";

const AddOrEditCasePageView = () => {
  const user = useSelector((state) => state.user.currentUser);
  const selectedCase = useSelector((state) => state.cases.selectedCase);
  const navigate = useNavigate();
  const isMobile = useIsMobile();

  const [title, setTitle] = useState(selectedCase?.title || "");
  const [anatomy, setAnatomy] = useState(selectedCase?.anatomy || "");
  const [requester, setRequester] = useState(selectedCase?.requester || "");
  const [dueDate, setDueDate] = useState(
    selectedCase ? parseDate(selectedCase.dueDate) : null
  );
  const [creationDate, setCreationDate] = useState(
    selectedCase ? parseDate(selectedCase.creationDate) : new Date()
  );
  const [expirationDate, setExpirationDate] = useState(
    selectedCase ? parseDate(selectedCase.expirationDate) : null
  );
  const [error, setError] = useState(null);

  const onSubmit = async (e) => {
    e.preventDefault();
    const updatedCreator = e.target.creator.value;

    if (
      [
        { value: title, name: "Title" },
        { value: anatomy, name: "Anatomy" },
        { value: requester, name: "Requester" },
        { value: dueDate, name: "Due Date" },
        { value: creationDate, name: "Creation Date" },
        { value: updatedCreator, name: "Creator" },
      ].some((field) => !field.value)
    ) {
      setError("Please fill in all required fields.");
      return;
    }

    if (!user?.id) {
      setError("User is not loaded correctly.");
      return;
    }

    try {
      if (selectedCase) {
        // Convert dates to milliseconds at midnight UTC
        const dueDateMs = new Date(dueDate).setHours(0, 0, 0, 0);
        const creationDateMs = new Date(creationDate).setHours(0, 0, 0, 0);
        const expirationDateMs = expirationDate
          ? new Date(expirationDate).setHours(0, 0, 0, 0)
          : null;

        const updatedCaseData = {
          id: selectedCase.id,
          title,
          anatomy,
          requester,
          dueDate: dueDateMs,
          creationDate: creationDateMs,
          expirationDate: expirationDateMs,
          creator: updatedCreator,
          status: selectedCase.status,
          users: selectedCase.users,
        };
        await set(ref(database, `cases/${selectedCase.id}`), updatedCaseData);
      } else {
        const newCaseKey = push(ref(database, `cases`)).key;

        // Convert dates to milliseconds at midnight UTC
        const dueDateMs = new Date(dueDate).setHours(0, 0, 0, 0);
        const creationDateMs = new Date(creationDate).setHours(0, 0, 0, 0);
        const expirationDateMs = expirationDate
          ? new Date(expirationDate).setHours(0, 0, 0, 0)
          : null;

        const newCaseData = {
          id: newCaseKey,
          title,
          anatomy,
          requester,
          dueDate: dueDateMs,
          creationDate: creationDateMs,
          expirationDate: expirationDateMs,
          creator: updatedCreator,
          status: Statuses.UPLOAD_DICOM,
          users: {
            [user.id]: {
              role: Roles.CREATOR,
              name: `${user.firstName} ${user.lastName}`,
            },
          },
        };

        // Create the case
        await set(ref(database, `cases/${newCaseKey}`), newCaseData);

        // Update user's cases map with timestamp as key
        const userRef = ref(database, `users/${user.id}`);
        const snapshot = await get(userRef);
        if (!snapshot.exists()) {
          setError("User not found.");
          return;
        }
        const userData = snapshot.val();
        const updatedCasesMap = {
          ...(userData.cases || {}),
          [Date.now()]: newCaseKey,
        };
        await update(userRef, { cases: updatedCasesMap });
      }
      navigate("/caselist");
    } catch (err) {
      console.error("Error saving case:", err);
      setError("Error saving case.");
    }
  };

  return (
    <div
      className={`px-4 pt-8 pb-8 min-h-screen ${
        isMobile ? "bg-white" : "flex items-center justify-center bg-gray-100"
      }`}
      style={{
        "--main-color": anatomyConstants.mainColor,
        "--main-color-bold": anatomyConstants.mainColorBold,
      }}
    >
      <div className="w-full max-w-2xl mx-auto p-10 rounded-2xl shadow-xl bg-white">
        <h2 className="text-2xl font-bold mb-6 pb-3 border-b-2 border-[#CBE1FA] text-center text-[var(--main-color)]">
          {selectedCase ? "Edit Case" : "Add New Case"}
        </h2>
        {error && (
          <p className="text-center text-red-500 font-semibold mb-4">{error}</p>
        )}
        <form
          className="space-y-5"
          onSubmit={(e) => {
            setError(null);
            onSubmit(e);
          }}
        >
          <div>
            <label
              htmlFor="title"
              className="block text-gray-700 font-medium mb-2"
            >
              Title <span className="text-red-500">*</span>
            </label>
            <input
              id="title"
              type="text"
              className="w-full px-4 py-3 border border-gray-200 rounded-lg bg-white text-gray-700 placeholder-gray-400 focus:outline-none focus:border-[var(--main-color)] focus:ring-2 focus:ring-[#CBE1FA] transition"
              placeholder="Enter title"
              required
              value={title}
              onChange={(e) => setTitle(e.target.value)}
            />
          </div>
          <div>
            <label
              htmlFor="anatomy"
              className="block text-gray-700 font-medium mb-2"
            >
              Anatomy <span className="text-red-500">*</span>
            </label>
            <input
              id="anatomy"
              type="text"
              className="w-full px-4 py-3 border border-gray-200 rounded-lg bg-white text-gray-700 placeholder-gray-400 focus:outline-none focus:border-[var(--main-color)] focus:ring-2 focus:ring-[#CBE1FA] transition"
              placeholder="Enter anatomy"
              required
              value={anatomy}
              onChange={(e) => setAnatomy(e.target.value)}
            />
          </div>
          <div>
            <label
              htmlFor="creator"
              className="block text-gray-700 font-medium mb-2"
            >
              Creator <span className="text-red-500">*</span>
            </label>
            <input
              id="creator"
              type="text"
              className="w-full px-4 py-3 border border-gray-200 rounded-lg bg-white text-gray-700 placeholder-gray-400 focus:outline-none focus:border-[var(--main-color)] focus:ring-2 focus:ring-[#CBE1FA] transition"
              placeholder="Enter creator"
              defaultValue={
                selectedCase
                  ? selectedCase.creator
                  : user
                  ? `${user.firstName} ${user.lastName}`
                  : ""
              }
              required
              name="creator"
            />
          </div>
          <div>
            <label
              htmlFor="requester"
              className="block text-gray-700 font-medium mb-2"
            >
              Requester <span className="text-red-500">*</span>
            </label>
            <input
              id="requester"
              type="text"
              className="w-full px-4 py-3 border border-gray-200 rounded-lg bg-white text-gray-700 placeholder-gray-400 focus:outline-none focus:border-[var(--main-color)] focus:ring-2 focus:ring-[#CBE1FA] transition"
              placeholder="Enter requester"
              required
              value={requester}
              onChange={(e) => setRequester(e.target.value)}
            />
          </div>
          <div>
            <label
              htmlFor="creationDate"
              className="block text-gray-700 font-medium mb-2"
            >
              Creation Date <span className="text-red-500">*</span>
            </label>
            <DatePicker
              selected={creationDate}
              onChange={(date) => setCreationDate(date)}
              dateFormat="dd/MM/yyyy"
              className="w-full px-4 py-3 border border-gray-200 rounded-lg bg-white text-gray-700 placeholder-gray-400 focus:outline-none focus:border-[var(--main-color)] focus:ring-2 focus:ring-[#CBE1FA] transition"
              placeholderText="Select creation date"
              required
            />
          </div>
          <div>
            <label
              htmlFor="dueDate"
              className="block text-gray-700 font-medium mb-2"
            >
              Due Date <span className="text-red-500">*</span>
            </label>
            <DatePicker
              selected={dueDate}
              onChange={(date) => setDueDate(date)}
              dateFormat="dd/MM/yyyy"
              className="w-full px-4 py-3 border border-gray-200 rounded-lg bg-white text-gray-700 placeholder-gray-400 focus:outline-none focus:border-[var(--main-color)] focus:ring-2 focus:ring-[#CBE1FA] transition"
              placeholderText="Select due date"
              required
            />
          </div>
          <div>
            <label
              htmlFor="expirationDate"
              className="block text-gray-700 font-medium mb-2"
            >
              Expiration Date
            </label>
            <DatePicker
              selected={expirationDate}
              onChange={(date) => setExpirationDate(date)}
              dateFormat="dd/MM/yyyy"
              className="w-full px-4 py-3 border border-gray-200 rounded-lg bg-white text-gray-700 placeholder-gray-400 focus:outline-none focus:border-[var(--main-color)] focus:ring-2 focus:ring-[#CBE1FA] transition"
              placeholderText="Select expiration date (optional)"
            />
          </div>
          <div className="pt-2">
            <button
              type="submit"
              style={{ color: "#fff", fontWeight: 500 }}
              className="w-full py-2 px-4 rounded-lg transition focus:outline-none bg-[var(--main-color)] hover:bg-[var(--main-color-bold)]"
            >
              {selectedCase ? "Update Case" : "Submit"}
            </button>
          </div>
          <p className="text-xs text-gray-500 text-center mt-2">
            <span className="text-red-500">*</span> Required fields.
          </p>
        </form>
      </div>
    </div>
  );
};

export default AddOrEditCasePageView;
